export const SKY_FIRE_FLOW = Object.freeze({ sampleSeconds: 1 / 30, maxParcels: 64 });
const finite = (point) => point && ['x', 'y', 'z'].every((axis) => Number.isFinite(point[axis]));
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const mix = (a, b, t) =>
  Object.fromEntries(['x', 'y', 'z'].map((axis) => [axis, a[axis] + (b[axis] - a[axis]) * t]));

/** Actual source/listener velocity in metres/second from two world positions.
 * Discontinuous or nonfinite time fails to rest rather than inventing motion. */
export function skyMeasuredVelocity(before, after, dt) {
  return finite(before) && finite(after) && Number.isFinite(dt) && dt > 0 && dt <= 0.2
    ? Object.fromEntries(['x', 'y', 'z'].map((axis) => [axis, (after[axis] - before[axis]) / dt]))
    : { x: 0, y: 0, z: 0 };
}

function parcel(breath, origin, velocity, emittedAt) {
  const axial = dot(velocity, breath.direction);
  // Arcade pressure calibration preserves the telegraphed forward speed while
  // retaining actual transverse emitter inertia. This is not a fluid solver.
  // An advancing nozzle cannot release a later parcel across the promised
  // target plane before the original0.8-second reaction interval has elapsed.
  const remaining = breath.reactionSeconds - emittedAt;
  const distance = dot(
    Object.fromEntries(['x', 'y', 'z'].map((axis) => [axis, breath.target[axis] - origin[axis]])),
    breath.direction,
  );
  const forward =
    remaining > 1e-8 ? Math.min(breath.speed, Math.max(0, distance / remaining)) : breath.speed;
  return {
    id: breath.nextParcel++,
    emittedAt,
    origin: { ...origin },
    velocity: Object.fromEntries(
      ['x', 'y', 'z'].map((axis) => [
        axis,
        velocity[axis] + breath.direction[axis] * (forward - axial),
      ]),
    ),
    age: 0,
    position: { ...origin },
    radius: breath.nearRadius,
  };
}

/** Initialize one inertial material sample; launch data is immutable thereafter.
 * Breath distances/radii are metres and clocks seconds. Caller owns ignition
 * and its already validated locked direction. No I/O or external clock is read. */
export function initializeSkyFireFlow(breath, velocity = { x: 0, y: 0, z: 0 }) {
  breath.nextParcel = 0;
  breath.nextEmission = SKY_FIRE_FLOW.sampleSeconds;
  breath.parcels = [
    parcel(breath, breath.origin, finite(velocity) ? velocity : { x: 0, y: 0, z: 0 }, 0),
  ];
  breath.emitting = true;
  breath.flowActive = true;
  breath.segments = [];
  return breath;
}

/** Advance emitted material independently of the current mouth. Each sample
 * coasts with its launch velocity; only new samples consume measured nozzle
 * motion. Fixed30Hz emission interpolation is independent of presentation FPS.
 * Emission lasts the authored duration, then visible/harmful material drains to
 * its finite range. Malformed steps do nothing; capacity is strictly bounded. */
export function stepSkyFireFlow(breath, dt, mouth, velocity, age = breath?.age + dt) {
  if (
    !breath ||
    !Array.isArray(breath.parcels) ||
    !finite(breath.origin) ||
    !finite(mouth) ||
    !Number.isFinite(age) ||
    age < dt ||
    !Number.isFinite(dt) ||
    dt <= 0 ||
    dt > 0.1 ||
    !(breath.speed > 0) ||
    !(breath.range > 0)
  )
    return;
  const before = age - dt,
    previousMouth = breath.origin;
  breath.age = age;
  const sourceVelocity = finite(velocity)
    ? velocity
    : skyMeasuredVelocity(previousMouth, mouth, dt);
  const until = Math.min(breath.age, breath.duration);
  while (breath.nextEmission <= until + 1e-9 && breath.parcels.length < SKY_FIRE_FLOW.maxParcels) {
    const at = breath.nextEmission;
    const origin = mix(previousMouth, mouth, Math.max(0, Math.min(1, (at - before) / dt)));
    breath.parcels.push(parcel(breath, origin, sourceVelocity, at));
    breath.nextEmission += SKY_FIRE_FLOW.sampleSeconds;
  }
  breath.origin = { ...mouth };
  breath.emitting = breath.age < breath.duration;
  breath.parcels = breath.parcels.filter(
    (sample) => (breath.age - sample.emittedAt) * breath.speed <= breath.range,
  );
  for (const sample of breath.parcels) {
    sample.age = breath.age - sample.emittedAt;
    sample.position = Object.fromEntries(
      ['x', 'y', 'z'].map((axis) => [
        axis,
        sample.origin[axis] + sample.velocity[axis] * sample.age,
      ]),
    );
    sample.radius =
      breath.nearRadius +
      (breath.farRadius - breath.nearRadius) *
        Math.min(1, (sample.age * breath.speed) / breath.range);
  }
  const points = breath.parcels;
  const vertices = breath.emitting
    ? [...points, { id: 'mouth', position: mouth, radius: breath.nearRadius }]
    : points;
  breath.segments = vertices
    .slice(1)
    .map((sample, index) => ({
      id: `${breath.attackId}:${vertices[index].id}`,
      from: { ...vertices[index].position },
      to: { ...sample.position },
      direction: { ...breath.direction },
      fromRadius: vertices[index].radius,
      toRadius: sample.radius,
      velocity: mix(
        vertices[index].velocity ?? sourceVelocity,
        sample.velocity ?? sourceVelocity,
        0.5,
      ),
    }))
    .filter(
      (segment) =>
        Math.hypot(...['x', 'y', 'z'].map((axis) => segment.to[axis] - segment.from[axis])) > 1e-8,
    );
  breath.length = Math.min(
    breath.range,
    Math.max(0, ...points.map((sample) => sample.age * breath.speed)),
  );
  breath.active = breath.emitting;
  breath.flowActive = breath.emitting || breath.segments.length > 0;
}

/** Exact shared tapered segment contact. Finite axial limits forbid invisible
 * extension past the advancing front. No post-launch direction correction,
 * enlarged target, camera geometry or renderer acknowledgement participates. */
export function skyFireFlowContact(breath, point, radius = 0) {
  if (!breath?.flowActive || !finite(point) || !Number.isFinite(radius) || radius < 0) return false;
  return (Array.isArray(breath.segments) ? breath.segments : []).some((segment) => {
    if (
      !finite(segment?.from) ||
      !finite(segment?.to) ||
      ![segment.fromRadius, segment.toRadius].every((value) => Number.isFinite(value) && value >= 0)
    )
      return false;
    const vector = Object.fromEntries(
      ['x', 'y', 'z'].map((axis) => [axis, segment.to[axis] - segment.from[axis]]),
    );
    const axis = segment.direction;
    if (!finite(axis)) return false;
    const length = dot(vector, axis);
    const delta = Object.fromEntries(
      ['x', 'y', 'z'].map((key) => [key, point[key] - segment.from[key]]),
    );
    const t = dot(delta, axis) / length;
    if (!(t >= 0 && t <= 1)) return false;
    const radial = Object.fromEntries(
      ['x', 'y', 'z'].map((key) => [key, delta[key] - vector[key] * t]),
    );
    const distance2 = dot(radial, radial);
    const width = segment.fromRadius + (segment.toRadius - segment.fromRadius) * t + radius;
    return distance2 < width * width;
  });
}

/** Bounded classical air Doppler ratio from measured world velocities (m/s).
 *343m/s is an explicit room-temperature approximation. Teleports/nonfinite
 * samples fail to unity; radial components and audible ratio are bounded so
 * game-scale motion cannot produce a startling or singular pitch excursion. */
export function skyFireDoppler(source, listener, sourceVelocity, listenerVelocity) {
  if (![source, listener, sourceVelocity, listenerVelocity].every(finite)) return 1;
  if ([sourceVelocity, listenerVelocity].some((v) => Math.hypot(v.x, v.y, v.z) > 120)) return 1;
  const delta = Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [axis, listener[axis] - source[axis]]),
  );
  const distance = Math.hypot(delta.x, delta.y, delta.z);
  if (distance < 0.01) return 1;
  const direction = Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [axis, delta[axis] / distance]),
  );
  const radial = (velocity) => Math.max(-30, Math.min(30, dot(velocity, direction)));
  return Math.max(
    0.94,
    Math.min(1.06, (343 - radial(listenerVelocity)) / (343 - radial(sourceVelocity))),
  );
}
